import React, { useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import GPSelect from '@gp/components/Select';
import GPSelectMultiple from '@gp/components/SelectMultiple';

import type { IBaseProps } from '@home/types/common';
import type { FilterItemType, SelectValuesType, IChangeHandler } from './types';

interface IFilterItemProps extends IBaseProps {
  options: FilterItemType;
  allOptions: FilterItemType[];
  selectValues: SelectValuesType;
  setSelectValues: React.Dispatch<React.SetStateAction<SelectValuesType>>;
  lastSelectValues: SelectValuesType;
  setLastSelectValues: React.Dispatch<React.SetStateAction<SelectValuesType>>;
  onChange: IChangeHandler | null;
}

const FilterItem: React.FC<IFilterItemProps> = (props) => {
  const { options, allOptions, selectValues, setSelectValues, lastSelectValues, setLastSelectValues, onChange } = props;

  const {
    refreshRenderKey,
    type,
    fieldName,
    value,
    option,
    className,
    style,
    label,
    disabled,
    placeholder,
    listWidth,
    ...others
  } = options;

  const handleFilterChange = useMemoizedFn((selectValue: string | string[], currentFilterOptions: FilterItemType) => {
    const { type, fieldName, option } = currentFilterOptions;
    let isStop = false;
    switch (type) {
      case 'singleselect': {
        selectValues[fieldName] = selectValue;
        break;
      }

      case 'multipleselect': {
        // 重复全选的效果不请求接口
        if (selectValue?.length === option?.length && !lastSelectValues?.[fieldName]?.length) {
          isStop = true;
          break;
        }
        // 当length是全选时，也设置成[]
        if (selectValue?.length === option?.length) {
          selectValues[fieldName] = [];
          break;
        }
        const newSelectValue = selectValue ? [...selectValue] : [];
        selectValues[fieldName] = newSelectValue;
        break;
      }

      default: {
        isStop = true;
        break;
      }
    }
    if (isStop) return;
    setSelectValues({ ...selectValues });
    setLastSelectValues({ ...selectValues });
    onChange?.(selectValues, fieldName);
  });

  // 初始化记录各筛选器value值
  useEffect(() => {
    if (!Array.isArray(allOptions)) return;
    const initSelectValues: SelectValuesType = {};
    for (let i = 0, l = allOptions?.length; i < l; i++) {
      const t = allOptions?.[i];
      initSelectValues[t?.fieldName] = t?.value;
    }
    setSelectValues(initSelectValues);
    setLastSelectValues(initSelectValues);
  }, [allOptions?.length]);

  // 通知刷新某个筛选器时
  useEffect(() => {
    selectValues[fieldName] = value;
    lastSelectValues[fieldName] = value;
    setSelectValues({ ...selectValues });
    setLastSelectValues({ ...lastSelectValues });
  }, [refreshRenderKey]);

  switch (type) {
    case 'singleselect': {
      return (
        <GPSelect
          style={style}
          className={className}
          label={label}
          listWidth={listWidth}
          searchable={true}
          disabled={disabled}
          options={option}
          value={selectValues?.[fieldName] as string}
          onChange={(v) => handleFilterChange(v, options)}
          {...others}
        />
      );
    }

    case 'multipleselect': {
      return (
        <GPSelectMultiple
          style={style}
          className={className}
          label={label}
          listWidth={listWidth}
          searchable={true}
          disabled={disabled}
          options={option}
          value={selectValues?.[fieldName] as string[]}
          onChange={(v) => handleFilterChange(v, options)}
          {...others}
        />
      );
    }

    default: {
      return null;
    }
  }
};

export default FilterItem;
